import React from 'react';
import { ContentRendererProps } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getPlatformPostType, isSeriesFromPostType } from '@/types/content.types';

// Define interfaces for the content structure
interface SocialPost {
  text: string;
  hashtags?: string[];
  calls_to_action?: string[];
  media?: string[];
  image_url?: string; // Added image_url property
}

interface SocialPostContent {
  posts?: SocialPost[];
  text?: string; // For backwards compatibility with single post format
  hashtags?: string[];
  platform?: string;
  media?: string[];
  platform_post_type?: string; // New field - replaces is_series
  is_series?: boolean; // Legacy field - kept for backward compatibility
  post_type?: string;
}

/**
 * Renderer for social post content type with support for both images and videos
 * Supports both new platform_post_type and legacy is_series fields
 */
export const SocialPostRenderer: React.FC<ContentRendererProps> = ({ content }) => {
  // console.log('SocialPostRenderer - Rendering social post:', content.name);

  // Extract social post specific data
  const contentJson = content.content_json as SocialPostContent;

  const { platform } = contentJson;

  // Get platform post type with backward compatibility fallback
  const platformPostType = getPlatformPostType(contentJson);

  // Determine if this is a series/multi-post (for display purposes)
  const isSeries = isSeriesFromPostType(platformPostType);
  
  // Handle both single post and thread/series format
  const posts = contentJson.posts || 
    (contentJson.text ? [{ 
      text: contentJson.text, 
      hashtags: contentJson.hashtags,
      media: contentJson.media
    }] : []);
  
  // Function to format post type for display
  const formatPostType = (postType: string): string => {
    const labels: Record<string, string> = {
      'single': 'Tweet',
      'thread': 'Thread',
      'single_image': 'Post',
      'carousel': 'Carousel',
      'reel': 'Reel',
      'story': 'Story',
      'video': 'Video',
      'live': 'Live',
      'photo': 'Photo',
      'link': 'Link',
      'text': 'Text Post',
      'event': 'Event',
      'document': 'Document',
      'poll': 'Poll',
      'newsletter': 'Newsletter',
      'image': 'Image',
      'article': 'Article'
    };
    return labels[postType] || postType.charAt(0).toUpperCase() + postType.slice(1);
  };

  // Function to check if a file is a video based on extension
  const isVideoFile = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v', '.ogv'];
    const extension = url.toLowerCase().split('.').pop();
    return videoExtensions.includes(`.${extension}`);
  };
  
  // Function to format text with hashtags highlighted
  const formatText = (text: string, hashtags: string[] = []) => {
    // If no hashtags, just return the text
    if (!hashtags || hashtags.length === 0) return text;
    
    // Create a string with all hashtags for highlighting
    const hashtagText = hashtags.map(tag => tag.startsWith('#') ? tag : `#${tag}`);
    
    // Return the text with hashtags highlighted
    return text.split(' ').map((word, index) => {
      // Check if the word is a hashtag (with or without the # symbol)
      const isHashtag = hashtagText.includes(word) || hashtagText.includes(`#${word}`);
      
      return isHashtag 
        ? <span key={index} className="text-blue-500 dark:text-blue-400 font-medium">{word} </span>
        : `${word} `;
    });
  };
  
  // Different styling based on platform
  const getPlatformStyles = () => {
    switch (platform?.toLowerCase()) {
      case 'twitter':
      case 'x':
        return 'bg-gray-100 dark:bg-gray-800 rounded-xl';
      case 'facebook':
        return 'bg-blue-50 dark:bg-blue-950 rounded-md';
      case 'instagram':
        return 'bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 rounded-md';
      case 'linkedin':
        return 'bg-blue-50 dark:bg-blue-950 rounded-md';
      default:
        return 'bg-gray-100 dark:bg-gray-800 rounded-md';
    }
  };
  
  // Format date for display
  const formatDate = () => {
    const date = new Date();
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <div className="max-w-2xl mx-auto p-4">
      {posts.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          No content available
        </div>
      ) : (
        <div className="space-y-4">
          {/* Post type badge - show for non-standard post types */}
          {isSeries && posts.length > 1 && (
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary" className="text-sm">
                {formatPostType(platformPostType)}
              </Badge>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {posts.length} posts
              </span>
            </div>
          )}
          
          {/* Render each post */}
          {posts.map((post, postIndex) => (
            <Card key={postIndex} className={`overflow-hidden ${getPlatformStyles()}`}>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  {/* Platform icon/name */}
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-full" />
                    <div>
                      <p className="font-medium">
                        {content.name.split(":")[0] || 'Social Post'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {platform || 'Social Post'} · {formatDate()}
                        {isSeries && posts.length > 1 && ` · ${postIndex + 1}/${posts.length}`}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Post content */}
                <div className="mb-4 whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {formatText(post.text || '', post.hashtags || [])}
                </div>
                
                {/* Post media - check for both media and image_url, support videos */}
                {((post.media && post.media.length > 0) || post.image_url) && (
                  <div className="mt-4 overflow-hidden rounded-md">
                    {(() => {
                      const mediaUrl = post.image_url || (post.media && post.media[0]);
                      if (mediaUrl && isVideoFile(mediaUrl)) {
                        return (
                          <video 
                            src={mediaUrl}
                            className="w-full h-auto object-cover"
                            controls
                            autoPlay
                            muted
                            playsInline
                            loop
                            preload="metadata"
                          >
                            Your browser does not support the video tag.
                          </video>
                        );
                      } else if (mediaUrl) {
                        return (
                          <img 
                            src={mediaUrl} 
                            alt="Post media" 
                            className="w-full h-auto object-cover"
                          />
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                
                {/* Call to action */}
                {post.calls_to_action && post.calls_to_action.length > 0 && (
                  <div className="mt-4 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    {post.calls_to_action[0]}
                  </div>
                )}
                
                {/* Hashtags display - only if they aren't already in the text */}
                {post.hashtags && post.hashtags.length > 0 && !post.text?.includes('#') && (
                  <div className="mt-4 text-sm">
                    {post.hashtags.map((tag: string, index: number) => (
                      <span 
                        key={index}
                        className="inline-block mr-2 text-blue-500 dark:text-blue-400 font-medium"
                      >
                        {tag.startsWith('#') ? tag : `#${tag}`}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialPostRenderer; 